import { useState, useEffect } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  useSidebar,
} from "./ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { 
  Briefcase, 
  Users, 
  Settings, 
  LogOut,
  User,
  ChevronUp,
  ChevronRight,
  Calendar,
  Bell,
  UsersRound,
  Cog,
  Building2,
  Heart,
  Gift,
  CreditCard,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, clearUserCache } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { TalentureLogo } from "./TalentureLogo";

const DashboardIcon = ({ className = "" }: { className?: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

interface CompanySidebarProps {
  activeRoute?: string;
}

export const CompanySidebar = ({ activeRoute }: CompanySidebarProps) => {
  const { user } = useAuth();
  const [currentRoute] = useLocation();
  const searchString = useSearch();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const route = activeRoute || currentRoute;

  const currentSection = new URLSearchParams(searchString).get("section") || "overview";

  const { data: membersResponse } = useQuery<{ success: boolean; data: Array<{ userId: string | null; stytchRole: string }> }>({
    queryKey: ['/api/companies/members'],
    queryFn: async () => {
      const response = await apiRequest('/api/companies/members');
      return response.json();
    },
    enabled: user?.role === 'company',
  });
  const sidebarMembers = Array.isArray(membersResponse) ? membersResponse : (Array.isArray(membersResponse?.data) ? membersResponse.data : []);
  const currentMember = sidebarMembers.find(m => m.userId === user?.id);
  const isCompanyAdmin = currentMember?.stytchRole === 'stytch_admin';
  const isCompanyAdminOrManager = isCompanyAdmin || currentMember?.stytchRole === 'hiring_manager';

  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}`
    : user?.email || "User";

  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.email?.[0]?.toUpperCase() || "U";

  const isCompanyProfileActive = route.startsWith("/company-profile");
  const isSettingsActive = route.startsWith("/settings");

  // Controlled collapsible state that syncs with active route
  const [profileOpen, setProfileOpen] = useState(isCompanyProfileActive);
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);

  // Sync collapsible state with route changes (auto-expand when navigating to route)
  useEffect(() => {
    if (isCompanyProfileActive) setProfileOpen(true);
  }, [isCompanyProfileActive]);

  useEffect(() => {
    if (isSettingsActive) setSettingsOpen(true);
  }, [isSettingsActive]);

  const companyProfileSections = [
    { id: "overview", label: "Overview", icon: Building2 },
    { id: "values", label: "Values", icon: Heart },
    { id: "benefits", label: "Benefits", icon: Gift },
  ];

  const BILLING_ENABLED = import.meta.env.VITE_BILLING_ENABLED !== 'false';

  const settingsSections = [
    { id: "calendar", label: "Calendar", icon: Calendar },
    ...(BILLING_ENABLED ? [{ id: "billing", label: "Billing", icon: CreditCard, adminOnly: true }] : []),
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "team", label: "Team", icon: UsersRound, adminOrManagerOnly: true },
    { id: "system", label: "System", icon: Cog },
  ];

  const simpleNavigationItems: Array<{
    path: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    isActive: boolean;
    badge?: string;
  }> = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: DashboardIcon,
      isActive: route === "/dashboard" || route === "/company-dashboard"
    },
    {
      path: "/jobs-overview",
      label: "Jobs",
      icon: Briefcase,
      isActive: route === "/jobs-overview" || route === "/job-posting" || route === "/job-edit" || route.startsWith("/job-view/")
    },
    {
      path: "/applications-board",
      label: "Candidates",
      icon: Users,
      isActive: route === "/applications-board"
    },
  ];

  const handleLogout = async () => {
    try {
      await clearUserCache();
      
      const response = await apiRequest('/api/auth/logout', {
        method: 'POST'
      });
      if (response.ok) {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      await clearUserCache();
      window.location.href = '/api/auth/logout';
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-base-gray-200">
      <SidebarHeader className="p-4 border-b border-base-gray-200">
        <TalentureLogo />
      </SidebarHeader>
      
      <SidebarContent className="px-3 py-4 group-data-[collapsible=icon]:px-0">
        <SidebarMenu>
          {simpleNavigationItems.map((item) => {
            const IconComponent = item.icon;
            
            return (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton 
                  asChild 
                  size="lg"
                  isActive={item.isActive}
                  tooltip={item.label}
                  className="h-11 px-3 rounded-lg font-medium"
                >
                  <Link to={item.path}>
                    <IconComponent className="w-5 h-5" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <Badge className="ml-auto bg-red-500 text-white text-xs">
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}

          {/* Company Profile - Collapsible (expanded) / Dropdown (collapsed) */}
          {isCollapsed ? (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    isActive={isCompanyProfileActive}
                    tooltip="Company Profile"
                    className="h-11 px-3 rounded-lg font-medium"
                  >
                    <User className="w-5 h-5" />
                    <span>Company Profile</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" sideOffset={4}>
                  <DropdownMenuLabel>Company Profile</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {companyProfileSections.map((section) => {
                    const IconComponent = section.icon;
                    const sectionUrl = section.id === "overview" 
                      ? "/company-profile" 
                      : `/company-profile?section=${section.id}`;
                    return (
                      <DropdownMenuItem key={section.id} asChild>
                        <Link to={sectionUrl} className="cursor-pointer">
                          <IconComponent className="mr-2 h-4 w-4" />
                          <span>{section.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          ) : (
            <Collapsible 
              asChild 
              open={profileOpen}
              onOpenChange={setProfileOpen}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    isActive={isCompanyProfileActive}
                    tooltip="Company Profile"
                    className="h-11 px-3 rounded-lg font-medium"
                  >
                    <User className="w-5 h-5" />
                    <span>Company Profile</span>
                    <ChevronRight className="ml-auto w-4 h-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {companyProfileSections.map((section) => {
                      const IconComponent = section.icon;
                      const sectionUrl = section.id === "overview" 
                        ? "/company-profile" 
                        : `/company-profile?section=${section.id}`;
                      const isActive = isCompanyProfileActive && currentSection === section.id;
                      
                      return (
                        <SidebarMenuSubItem key={section.id}>
                          <SidebarMenuSubButton 
                            asChild 
                            isActive={isActive}
                            className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent hover:text-base-primary"
                          >
                            <Link to={sectionUrl}>
                              <IconComponent className="w-4 h-4" />
                              <span>{section.label}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )}

          {/* Settings - Collapsible (expanded) / Dropdown (collapsed) */}
          {isCollapsed ? (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    isActive={isSettingsActive}
                    tooltip="Settings"
                    className="h-11 px-3 rounded-lg font-medium"
                  >
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" sideOffset={4}>
                  <DropdownMenuLabel>Settings</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {settingsSections.map((section) => {
                    if ('adminOnly' in section && section.adminOnly && !isCompanyAdmin) {
                      return null;
                    }
                    if ('adminOrManagerOnly' in section && section.adminOrManagerOnly && !isCompanyAdminOrManager) {
                      return null;
                    }
                    const IconComponent = section.icon;
                    const sectionUrl = section.id === "calendar" 
                      ? "/settings" 
                      : `/settings?section=${section.id}`;
                    return (
                      <DropdownMenuItem key={section.id} asChild>
                        <Link to={sectionUrl} className="cursor-pointer">
                          <IconComponent className="mr-2 h-4 w-4" />
                          <span>{section.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          ) : (
            <Collapsible 
              asChild 
              open={settingsOpen}
              onOpenChange={setSettingsOpen}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    isActive={isSettingsActive}
                    tooltip="Settings"
                    className="h-11 px-3 rounded-lg font-medium"
                  >
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                    <ChevronRight className="ml-auto w-4 h-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {settingsSections.map((section) => {
                      if ('adminOnly' in section && section.adminOnly && !isCompanyAdmin) {
                        return null;
                      }
                      if ('adminOrManagerOnly' in section && section.adminOrManagerOnly && !isCompanyAdminOrManager) {
                        return null;
                      }
                      
                      const IconComponent = section.icon;
                      const sectionUrl = section.id === "calendar" 
                        ? "/settings" 
                        : `/settings?section=${section.id}`;
                      const isActive = isSettingsActive && 
                        (section.id === "calendar" ? !searchString || currentSection === "calendar" : currentSection === section.id);
                      
                      return (
                        <SidebarMenuSubItem key={section.id}>
                          <SidebarMenuSubButton 
                            asChild 
                            isActive={isActive}
                            className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent hover:text-base-primary"
                          >
                            <Link to={sectionUrl}>
                              <IconComponent className="w-4 h-4" />
                              <span>{section.label}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )}
        </SidebarMenu>
      </SidebarContent>
      
      <SidebarFooter className="p-2 border-t border-base-gray-200 group-data-[collapsible=icon]:px-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="w-full data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="w-8 h-8 group-data-[collapsible=icon]:hidden">
                    <AvatarImage src={user?.profileImageUrl || undefined} alt={displayName} />
                    <AvatarFallback className="bg-base-primary text-white text-sm">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left text-sm leading-tight min-w-0 group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-medium block">{displayName}</span>
                    <span className="truncate text-xs text-muted-foreground block">Pro Account</span>
                  </div>
                  <ChevronUp className="w-4 h-4 ml-auto group-data-[collapsible=icon]:ml-0" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="w-56"
                side="top"
                align="end"
                sideOffset={8}
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{displayName}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/company-profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="cursor-pointer text-red-600 hover:text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
